import useEmblaCarousel from 'embla-carousel-react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { ComponentProps, useCallback, useEffect, useState } from 'react'
import { webchatClasses } from '../../../styles/classes'
import { CarouselBlock, type CommonBlockProps } from '../../../types'
import { Block } from '../../Block'

type Props = CarouselBlock & CommonBlockProps & ComponentProps<'div'>
type EmblaCarouselType = ReturnType<typeof useEmblaCarousel>[1]

export const Carousel = ({
  ref,
  blocks,
  direction,
  sender,
  timestamp,
  messageId,
  isReadOnly,
  sendMessage,
  addMessageFeedback,
}: Props) => {
  const [carouselRef, api] = useEmblaCarousel({ skipSnaps: true, loop: true })

  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true)
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true)

  const scrollPrev = useCallback(() => api?.scrollPrev(), [api])
  const scrollNext = useCallback(() => api?.scrollNext(), [api])

  const {
    message: {
      blocks: { carousel: carouselStyles },
    },
  } = webchatClasses

  const onSelect = useCallback(
    (api: EmblaCarouselType) => {
      setPrevBtnDisabled(!api?.canScrollPrev() || false)
      setNextBtnDisabled(!api?.canScrollNext() || false)
    },
    [api]
  )

  useEffect(() => {
    if (!api) return
    onSelect(api)
    api.on('reInit', onSelect)
    api.on('select', onSelect)
  }, [api, onSelect])

  return (
    <div {...carouselStyles?.container} data-container="carousel" ref={carouselRef}>
      <div ref={ref} {...carouselStyles?.slidesContainer}>
        {blocks.map((block, index) => (
          <Block key={index} block={{ ...block, direction, timestamp, sender, messageId, sendMessage, isReadOnly }} />
        ))}
      </div>
      {blocks.length > 1 && (
        <>
          <ChevronLeft
            {...carouselStyles?.backButton}
            data-disabled={prevBtnDisabled ? '' : undefined}
            onClick={scrollPrev}
          />

          <ChevronRight
            {...carouselStyles?.nextButton}
            data-disabled={nextBtnDisabled ? '' : undefined}
            onClick={scrollNext}
          />
        </>
      )}
    </div>
  )
}

Carousel.displayName = 'Carousel'
